<template>
  <div>
    <h1>Trade Lane Operational Metrics</h1>
    <b-row>
      <b-col cols="12">
        <filters
          @search="applyFilter"
          @berths-loaded="handleBerthsLoaded"
          :selected-berths="selectedBerths"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12">
        <model-voyage-chart
          class="port-card-narrow"
          :model-voyages="modelVoyages"
          :stats="movementsStat"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12">
        <b-card no-body class="port-card">
          <template #header>Voyage tracks (most recent voyages)</template>
          <b-card-body body-class="p-0">
            <port-tradeflows-map
              :berths="berths"
              :history-voyages="true"
              :voyage-parameters="voyageParameters"
              @berth-updated="handleBerthUpdated" />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6">
        <segments-chart
          class="port-card"
          title="Top Commodities (volume split by commodity)"
          :show-percentage="true"
          :items="commodityStat"
        />
      </b-col>

      <b-col cols="6">
        <segments-chart
          class="port-card"
          title="Top Segments (volume split by vessel segment)"
          :show-percentage="true"
          :items="segmentStat"
        />
      </b-col>
    </b-row>

    <h1>Detailed Historical Trade Lane Operational Statistics</h1>
    <b-row>
      <b-col cols="12">
        <periodic-stats
          title="Volume of exports"
          class="port-card"
          yAxisLabel="Volume (metric tons)"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.volume.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'volume',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <periodic-stats
          title="Voyage count"
          class="port-card"
          yAxisLabel="# of voyages"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.voyages.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'voyages',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <periodic-stats
          title="Voyage duration (total days)"
          class="port-card"
          yAxisLabel="total days"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.avgTimeTotalVoyage.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'avgTimeTotalVoyage',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <periodic-stats
          title="Voyage duration (days at sea)"
          class="port-card"
          yAxisLabel="days at sea"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="true"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.steamingDays.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'steamingDays',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
    </b-row>

    <!-- anchorages stat -->
    <b-row>
      <b-col cols="6">
        <!-- Load days waiting -->
        <periodic-stats
          title="Average waiting days at anchorage (load port)"
          class="port-card"
          yAxisLabel="Average time in anchorage (days)"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="false"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.avgTimeAnchorageLoad.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'avgTimeAnchorageLoad',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
      <b-col cols="6">
        <!-- Discharge days waiting -->
        <periodic-stats
          title="Average waiting days at anchorage (discharge port)"
          class="port-card"
          yAxisLabel="Average time in anchorage (days)"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="false"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.avgTimeAnchorageDischarge.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'avgTimeAnchorageDischarge',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
    </b-row>

    <!-- berths stat -->
    <b-row>
      <b-col cols="6">
        <!-- Load days berth -->
        <periodic-stats
          title="Average loading days at berth"
          class="port-card"
          yAxisLabel="Average time in berth (days)"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="false"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.avgTimeBerthLoad.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'avgTimeBerthLoad',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
      <b-col cols="6">
        <!-- Discharge days waiting -->
        <periodic-stats
          title="Average discharging days at berth"
          class="port-card"
          yAxisLabel="Average time in berth (days)"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="false"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.avgTimeBerthDischarge.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'avgTimeBerthDischarge',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
    </b-row>

    <!-- ports stat -->
    <b-row>
      <b-col cols="6">
        <!-- Load days waiting -->
        <periodic-stats
          title="Total port call duration (load port)"
          class="port-card"
          yAxisLabel="Average total time in port (days)"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="false"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.avgTimePortLoad.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'avgTimePortLoad',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
      <b-col cols="6">
        <!-- Discharge days waiting -->
        <periodic-stats
          title="Total port call duration (discharge port)"
          class="port-card"
          yAxisLabel="Average total time in port (days)"
          values-format="float1"
          :seasonal="true"
          :grouped="true"
          :stacked="false"
          :data-labels="true"
          :range-choices="rangeChoices"
          :stats="periodicStat.avgTimePortDischarge.data"
          @search="
            parameters => {
              applyFilter({
                _periodicStatSection: 'avgTimePortDischarge',
                _periodicStatRange: parameters.range,
                _periodicStatGroup: parameters.group
              });
            }
          "
          :apiRequestIsPerforming="!!getActiveRequestsCount"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12" class="d-flex">
        <voyages
          :disabled="!filtersReady"
          :export-types="['csv']"
          :export-loading="voyages.exportLoading"
          @export="handleVoyagesExport"
          :items="voyages.items"
          :sorting="allVoyagesLoaded"
          :currentPage="voyages.currentPage"
          @page-changed="voyagesPageChanged"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import getNested from 'lodash-es/get';

import { mapActions, mapGetters } from 'vuex';
import Filters from '@/components/portscomparison/Filters';
import SegmentsChart from '@/components/widgets/SegmentsChart';
import PeriodicStats from '@/components/tradeflows/PeriodicStats';
import PortTradeflowsMap from '@/components/porttradeflows/PortTradeflowsMap';
import Voyages from '@/components/tradeflows/Voyages';
import ModelVoyageChart from '@/components/tradeflows/ModelVoyageChart';
import moment from 'moment';
import { downloadAsFileByURL } from '@/shared/download-utils';
import isEmpty from 'lodash-es/isEmpty';

export default {
  name: 'ports-comparison',

  components: {
    ModelVoyageChart,
    Voyages,
    PortTradeflowsMap,
    PeriodicStats,
    SegmentsChart,
    Filters
  },

  data() {
    return {
      commodityStat: [],
      segmentStat: [],
      destinationsStat: {
        countries: {},
        ports: {}
      },
      movementsStat: {},
      modelVoyages: [],

      periodicStat: {
        volume: {
          data: [],
          group: null,
          range: 'weekly',
          metric: null,
        },
        voyages: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'count'
        },
        avgTimeAnchorageLoad: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'average_load_days_in_anchorage'
        },
        avgTimeAnchorageDischarge: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'average_discharge_days_in_anchorage'
        },
        avgTimeBerthLoad: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'average_load_days_in_berth'
        },
        avgTimeBerthDischarge: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'average_discharge_days_in_berth'
        },
        avgTimePortLoad: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'average_load_days_in_port'
        },
        avgTimePortDischarge: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'average_discharge_days_in_port'
        },
        steamingDays: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'days_steaming'
        },
        avgTimeTotalVoyage: {
          data: [],
          group: null,
          range: 'weekly',
          metric: 'average_voyage_duration_days'
        },
      },

      initialLoadRequestsCount: 10, // requests amount when filter is changed
      preloadRequestsCount: 1, // requests amount which will be performed on load
      preloadPagesRange: 5, // if (totalPages - currentPage) < preloadPagesRange
      // load another "preloadRequestsCount" requests
      voyages: {
        items: [],
        apiItemsPerPage: 50, // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null, // or maxPage, it doesn't matter since pages are starts from "1"
        apiCurrentPage: null,
        currentPage: null,
        exportLoading: false,
      },

      rangeChoices: [
        {text: 'Weekly', value: 'weekly'},
        {text: 'Monthly', value: 'monthly'},
        {text: 'Quarterly', value: 'quarter'},
        {text: 'Annual', value: 'year'}
      ],

      apiFilterValues: {},
      berths: [],
      selectedBerths: {
        load: [],
        discharge: []
      }
    };
  },

  methods: {
    ...mapActions('api/tradeflows', [
      'getCountriesStat',
      'getPortsStat',
      'getMovementsStat',
      'getVoyagesPeriodicStat',
      'getCommodityStat',
      'getVesselsStat',
      'getVoyages',
      'getModelVoyage'
    ]),

    async applyFilter(apiFilterValues) {
      // console.log('apply filter', JSON.stringify(apiFilterValues, null, 2));

      window.analytics.track('PortFlowSearch');
      this.apiFilterValues = {...this.apiFilterValues, ...apiFilterValues};
      this.selectedBerths.load = this.apiFilterValues.loadBerthId;
      this.selectedBerths.discharge = this.apiFilterValues.dischargeBerthId;

      if(this.filtersReady) {
        const periodicSectionName = apiFilterValues._periodicStatSection;
        if(periodicSectionName) {
          // periodic variants possible here: volume, voyages, avgtime (load/discharge)
          await this.loadPeriodicStats(apiFilterValues);
        } else {
          // we don't need "range" fields in these sections
          let parameters = this.getBaseFilterParameters();
          await this.loadCommodityStat(parameters);
          await this.loadSegmentStat(parameters);
          await this.loadMovementsStat(parameters);

          // model voyages
          await this.loadModelVoyage(parameters);

          // load all periodic stats
          await this.loadPeriodicStats(apiFilterValues);
          await this.loadVoyages(parameters);
        }
      } else {
        this.resetDashboardData();
      }
    },

    handleBerthUpdated(berth) {
      const targetBerths = this.selectedBerths[berth.direction];

      if(berth.selected && !targetBerths.includes(berth.value)) {
        targetBerths.push(berth.value);
      }
      if(!berth.selected && targetBerths.includes(berth.value)) {
        this.selectedBerths[berth.direction] = targetBerths.filter(
          berthId => berthId !== berth.value
        );
      }
    },

    handleBerthsLoaded(berths) {
      this.berths = berths;
    },

    async loadCommodityStat(filterParameters) {
      this.commodityStat = await this.getCommodityStat(filterParameters);
    },

    async loadSegmentStat(filterParameters) {
      this.segmentStat = await this.getVesselsStat(filterParameters);
    },

    async loadTopStat(filterParameters) {
      this.destinationsStat.countries = await this.getCountriesStat(filterParameters);
      this.destinationsStat.ports = await this.getPortsStat(filterParameters);
    },

    async loadMovementsStat(filterParameters) {
      this.movementsStat = (await this.getMovementsStat(filterParameters)) || [];
    },

    async loadModelVoyage(filterParameters) {
      this.modelVoyages = [];
      const response = (await this.getModelVoyage(filterParameters)) || [];
      this.modelVoyages = getNested(response, 'data.modelVoyages', []);
    },

    async loadPeriodicStats(filterParameters) {
      let targetSectionNames;
      let currentSectionName = filterParameters._periodicStatSection;

      if(currentSectionName) {
        let currentSection = this.periodicStat[currentSectionName];
        currentSection.range = filterParameters._periodicStatRange;
        currentSection.group = filterParameters._periodicStatGroup;
        targetSectionNames = [currentSectionName];
      } else {
        targetSectionNames = Object.keys(this.periodicStat);
      }

      for(let sectionName of targetSectionNames) {
        const currentSection = this.periodicStat[sectionName];
        let parameters = this.getParametersForSection(currentSection);
        currentSection.data = [];
        let response = (await this.getVoyagesPeriodicStat(parameters)) || {};
        currentSection.data = response.data || [];
      }
    },

    getParametersForSection(currentSection) {
      return {
        ...this.getBaseFilterParameters(),
        range: currentSection.range,
        group: currentSection.group,
        metric: currentSection.metric
      };
    },

    getBaseFilterParameters() {
      let parameters = {};
      Object.keys(this.apiFilterValues).forEach(fieldName => {
        if(!fieldName.startsWith('_')) {
          parameters[fieldName] = this.apiFilterValues[fieldName];
        }
      });
      return parameters;
    },

    async loadVoyages(filterParameters) {
      // it's possible to have a lot of pages here
      // they are loaded async to prevent interface lags

      this.voyages.items = [];
      this.voyages.currentPage = 1;
      await this.fetchVoyages(
        {...filterParameters, page: 1},
        this.initialLoadRequestsCount
      );
    },

    async voyagesPageChanged(currentPage, totalPages) {
      // voyages on demand loading, overview:
      // 1. detect when a current page is less than "preloadPagesRange" value
      // 2. add items which are received by "preloadRequestsCount" requests

      if(this.getActiveRequestsCount) {
        return; // prevent multiple requests
      }

      // calculate an API page number to start
      let apiPagesLoaded =
        this.voyages.items.length / this.voyages.apiItemsPerPage;

      if(
        totalPages - currentPage < this.preloadPagesRange &&
        Number.isInteger(apiPagesLoaded)
      ) {
        let parameters = this.getBaseFilterParameters();
        await this.fetchVoyages(
          {...parameters, page: apiPagesLoaded + 1},
          this.preloadRequestsCount
        );
        // b-pagination resets a current page when data is changed we have to restore it this way
        // https://github.com/bootstrap-vue/bootstrap-vue/issues/3372
        this.voyages.currentPage = currentPage;
      }
    },

    async fetchVoyages(filterParameters, requestsCount) {
      let countdown = requestsCount; // no more than countdown requests
      let parameters = {...filterParameters};
      let response = null;

      do {
        response = (await this.getVoyages(parameters)) || {};

        if(response.data && response.data.data) {
          this.voyages.items.push.apply(this.voyages.items, response.data.data);
          this.voyages.apiTotalPages = response.data.maxPage;
          this.voyages.apiCurrentPage = response.data.currentPage;
          parameters.page += 1;
        } else {
          break;
        }
        countdown--;
      } while (parameters.page <= response.data.maxPage && countdown);
    },

    async handleVoyagesExport(format) {
      this.voyages.exportLoading = true;
      const parameters = {
        format,
        limit: 1000000,
        ...this.getBaseFilterParameters(),
      };
      const response = await this.getVoyages(parameters);

      const timestamp = moment().format('YYYY-MM-DD-HH-mm');
      downloadAsFileByURL(
        getNested(response, 'data.csv', null),
        `port-tradeflows-voyages-export_${timestamp}.${format}`
      );
      this.voyages.exportLoading = false;
    },

    resetDashboardData() {
      this.commodityStat = [];
      this.destinationsStat = {countries: {}, ports: {}};
      this.movementsStat = {};
      this.modelVoyages = [];
      this.segmentStat = [];

      Object.keys(this.periodicStat).forEach(
        sectionName => (this.periodicStat[sectionName].data = [])
      );

      this.voyages.items = [];
      this.voyages.apiTotalPages = null;
      this.voyages.currentPage = null;
    }
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),

    filtersReady() {
      return (
        !isEmpty(this.apiFilterValues.loadBerthId) &&
        !isEmpty(this.apiFilterValues.dischargeBerthId)
      );
    },

    allVoyagesLoaded() {
      return (
        this.voyages.apiTotalPages &&
        this.voyages.apiCurrentPage &&
        this.voyages.apiTotalPages === this.voyages.apiCurrentPage
      );
    },

    voyageParameters() {
      const loadPortId = getNested(this, 'apiFilterValues.loadPortId', null);
      const dischargePortId = getNested(
        this,
        'apiFilterValues.dischargePortId',
        null
      );

      return {
        loadPortId: loadPortId ? [loadPortId] : [],
        dischargePortId: dischargePortId ? [dischargePortId] : [],
        segment: this.apiFilterValues.segment,
        commodityId: this.apiFilterValues.commodity,
        fromDate: this.apiFilterValues.fromDate,
        toDate: this.apiFilterValues.toDate,
        parceling: this.apiFilterValues.parceling,
      };
    }
  }
};
</script>

<style scoped>
.port-card >>> .card-body {
  /* deep-scoped style, affects child components */
  height: 370px;
}

.port-card-narrow >>> .card-body {
  /* deep-scoped style, affects child components */
  min-height: 220px;
}

.port-card >>> .chart-container {
  height: 410px;
}
</style>
